import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AdfRenderer from './AdfRenderer';
import SupportAttachmentList from './SupportAttachmentList';
import SupportAttachmentUploadButton from './SupportAttachmentUploadButton';

const PRIMARY = '#f25d27';

const PRIORITY_COLORS = {
  'Muito Alto': { bg: '#F6D6D6', text: '#7A1515' },
  'Alto': { bg: '#FBE7E7', text: '#A62929' },
  'Médio': { bg: '#FFF4A1', text: '#776A0A' },
  'Baixo': { bg: '#33C60E26', text: '#134C24' },
  'Muito Baixo': { bg: '#33C60E1A', text: '#134C24' },
  'Não definida': { bg: '#F2F2F2', text: '#413D3D' },
};

const STATUS_STEPS = ['Em espera', 'Em andamento', 'Concluído'];

function statusToStep(statusCategoryKey) {
  if (statusCategoryKey === 'done') return 2;
  if (statusCategoryKey === 'indeterminate') return 1;
  return 0;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}>
      <Box sx={{ color: '#aaa', mt: '2px', flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.88rem', color: '#333' }}>{value}</Typography>
      </Box>
    </Box>
  );
}

/**
 * @param {{
 *   issue: import('../../types/support').SupportIssueViewModel;
 *   onIssueChange: (issue: import('../../types/support').SupportIssueViewModel) => void;
 * }} props
 */
export default function SupportIssueInfoTab({ issue, onIssueChange }) {
  const priorityColor = PRIORITY_COLORS[issue.priority] || PRIORITY_COLORS['Não definida'];
  const activeStep = statusToStep(issue.statusCategoryKey);

  function handleAttachmentsChange(newAttachments) {
    onIssueChange({ ...issue, attachments: newAttachments });
  }

  function handleUploadedAttachments(newOnes) {
    onIssueChange({ ...issue, attachments: [...issue.attachments, ...newOnes] });
  }

  function handleAttachmentError(msg) {
    // Erro exibido internamente no componente de upload
    console.warn(msg);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Título + prioridade */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#222', flex: 1 }}>
          {issue.title}
        </Typography>
        <Chip
          label={issue.priority}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            background: priorityColor.bg,
            color: priorityColor.text,
          }}
        />
      </Box>

      {/* Stepper de status */}
      <Box>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            '& .MuiStepLabel-label': { fontSize: '0.75rem' },
            '& .MuiStepIcon-root.Mui-active': { color: PRIMARY },
            '& .MuiStepIcon-root.Mui-completed': { color: PRIMARY },
          }}
        >
          {STATUS_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Divider />

      {/* Informações do autor */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <InfoRow icon={<PersonOutlineIcon sx={{ fontSize: '1rem' }} />} label="Autor" value={issue.authorName} />
        <InfoRow icon={<EmailOutlinedIcon sx={{ fontSize: '1rem' }} />} label="E-mail" value={issue.authorEmail} />
        <InfoRow icon={<CalendarTodayOutlinedIcon sx={{ fontSize: '1rem' }} />} label="Data de criação" value={formatDate(issue.createdAt)} />
        <InfoRow icon={<UpdateIcon sx={{ fontSize: '1rem' }} />} label="Última atualização" value={formatDate(issue.updatedAt)} />
      </Box>

      <Divider />

      {/* Descrição */}
      {issue.descriptionAdf && (
        <Box>
          <Typography sx={{ fontSize: '0.75rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500, mb: 0.75 }}>
            Descrição
          </Typography>
          <AdfRenderer document={issue.descriptionAdf} />
        </Box>
      )}

      <Divider />

      {/* Anexos */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <AttachFileIcon sx={{ fontSize: '1rem', color: '#888' }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>
              Anexos
            </Typography>
          </Box>
          <SupportAttachmentUploadButton
            issueExternalId={issue.issueExternalId}
            onUploaded={handleUploadedAttachments}
            onError={handleAttachmentError}
          />
        </Box>
        <SupportAttachmentList
          issueExternalId={issue.issueExternalId}
          attachments={issue.attachments}
          onAttachmentsChange={handleAttachmentsChange}
        />
      </Box>
    </Box>
  );
}
